import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Heart, ArrowRight, Sparkles, Quote } from "lucide-react";
import { Container, Reveal, Overline, SectionHeading, Counter } from "../components/common";
import { Marquee } from "../components/Marquee";
import { DonateSection } from "../components/DonateSection";
import { Testimonials } from "../components/Testimonials";
import { ICONS } from "../components/icons";
import { IMPACT_STATS, PROGRAMS, MANIFESTO, IMAGES } from "../lib/content";
import { DEFAULT_PARTNERS, groupPartnersByType } from "../lib/partnerLogos";
import { fetchPartners, fetchImpactStats, fetchPrograms } from "../lib/api";
import { mapImpactStatRows, mapProgramRows } from "../lib/dynamicMappers";
import { useApiData } from "../hooks/useApiData";

const HEADLINE = ["12 years of", "Healing Hearts with", "Helping Hands."];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex items-end bg-navy overflow-hidden" data-testid="home-hero">
      <motion.div style={{ scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="Children learning in a supported government school" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy/80 to-transparent" />
        <div className="absolute inset-0 grain opacity-[0.08]" />
      </motion.div>

      <Container className="relative pb-16 pt-32 sm:pb-24">
        <motion.div style={{ y }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 glass border border-white/15 px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] text-teal-100">
              <Sparkles className="h-3.5 w-3.5 text-coral" /> Est. 2014 · Rooted in Telangana & Andhra Pradesh · Reaching all India
            </span>
          </motion.div>

          <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-[5.5rem] font-extrabold text-white leading-[1.02] tracking-tight max-w-4xl">
            {HEADLINE.map((line, i) => (
              <MaskedLine key={i} delay={0.25 + i * 0.14}>
                {i === 2 ? (
                  <span>
                    Helping <span className="text-coral">Hands.</span>
                  </span>
                ) : (
                  line
                )}
              </MaskedLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="mt-6 max-w-xl text-lg text-white/80 leading-relaxed"
          >
            Empowering underprivileged children, girl children, single women and elders through
            education, healthcare, skill development and dignity — directly on the ground across
            Telangana & Andhra Pradesh, with initiatives extending throughout India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/get-involved#donate"
              data-testid="hero-donate-button"
              className="inline-flex items-center gap-2 rounded-full bg-coral px-7 py-4 text-base font-semibold text-white shadow-soft transition-all hover:bg-coral-700 hover:-translate-y-0.5"
            >
              <Heart className="h-5 w-5 fill-white" /> Donate Now
            </Link>
            <Link
              to="/programs"
              data-testid="hero-programs-button"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 glass border border-white/20 px-7 py-4 text-base font-semibold text-white transition-all hover:bg-white/20"
            >
              Our Impact <ArrowRight className="h-5 w-5" />
            </Link>
          </motion.div>
        </motion.div>
      </Container>
    </section>
  );
};

const ImpactStrip = () => {
  const stats = useApiData(fetchImpactStats, IMPACT_STATS, mapImpactStatRows);

  return (
    <section className="py-20 lg:py-28 bg-ivory" data-testid="impact-section">
      <Container>
        <SectionHeading
          overline="Our Impact in Numbers"
          title="Twelve years, measured in real lives changed"
          subtitle="Every figure below is a family reached, a child in school, a woman standing on her own."
          center
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.05}>
              <div className="group h-full rounded-3xl bg-white p-6 lg:p-7 shadow-soft border border-slate-100 hover:border-teal-200 hover:shadow-card transition-all hover:-translate-y-1">
                <div className="font-display text-4xl lg:text-5xl font-extrabold text-teal-700">
                  <Counter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-3 font-display font-bold text-navy leading-snug">{s.label}</div>
                <div className="mt-1 text-sm text-slate-500">{s.sub}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
};

const Manifesto = () => (
  <section className="py-20 lg:py-28 bg-white" data-testid="manifesto-section">
    <Container>
      <SectionHeading
        overline="Our Sacred Core"
        title="A manifesto for grassroots dignity"
        subtitle="Four beliefs that guide every rupee, every camp and every classroom we touch."
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {MANIFESTO.map((m, i) => (
          <Reveal key={m.num} delay={i * 0.08}>
            <div className="h-full rounded-3xl border border-slate-100 bg-offwhite p-7 hover:bg-teal-50 transition-colors">
              <span className="font-mono text-sm text-coral font-semibold">{m.num}</span>
              <div className="mt-4 h-px w-10 bg-teal-200" />
              <h3 className="mt-4 font-display text-xl font-bold text-navy">{m.title}</h3>
              <p className="mt-3 text-slate-600 leading-relaxed">{m.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </section>
);

const FeaturedPrograms = () => {
  const programs = useApiData(fetchPrograms, PROGRAMS, mapProgramRows);

  return (
    <section className="py-20 lg:py-28 bg-offwhite" data-testid="programs-section">
      <Container>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading
            overline="6 Pillars of Change"
            title="Programs that nurture the whole community"
            subtitle="From a child's first classroom to an elder's dignity — holistic, sustained care."
          />
          <Link
            to="/programs"
            data-testid="view-all-programs"
            className="inline-flex items-center gap-2 rounded-full border border-teal-200 px-5 py-3 text-sm font-semibold text-teal-700 hover:bg-teal-700 hover:text-white hover:border-teal-700 transition-colors"
          >
            View all programs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {programs.map((p, i) => {
            const Icon = ICONS[p.icon];
            return (
              <Reveal key={p.id} delay={(i % 4) * 0.06}>
                <Link
                  to="/programs"
                  data-testid={`home-program-${p.id}`}
                  className="group block h-full rounded-3xl bg-white overflow-hidden shadow-soft border border-slate-100 hover:shadow-card hover:-translate-y-1 transition-all"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                    <span className="absolute top-3 left-3 grid place-items-center h-10 w-10 rounded-xl bg-white/90 glass text-teal-700">
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="absolute bottom-3 left-3 font-mono text-[11px] uppercase tracking-wider text-white/90">{p.tag}</span>
                  </div>
                  <div className="p-5">
                    <h3 className="font-display font-bold text-lg text-navy group-hover:text-teal-700 transition-colors">{p.name}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
                  </div>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </Container>
    </section>
  );
};

const EditorialQuote = () => (
  <section className="py-20 lg:py-28 bg-white" data-testid="quote-section">
    <Container>
      <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <Reveal className="lg:col-span-6">
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-card">
            <img src={IMAGES.womanHelping} alt="A volunteer caring for a child" className="h-full w-full object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-navy/10 rounded-3xl" />
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6">
          <Quote className="h-10 w-10 text-coral" />
          <p className="mt-5 font-serif text-3xl sm:text-4xl italic text-navy leading-snug text-balance">
            “We don't hand out charity. We hand back dignity — to a child who returns to school,
            to a mother who earns her own living, to an elder who is not forgotten.”
          </p>
          <div className="mt-7 flex items-center gap-4">
            <div className="h-12 w-12 rounded-full bg-teal-700 grid place-items-center text-white font-display font-bold">JF</div>
            <div>
              <div className="font-display font-bold text-navy">Reddygari Bhaskar Reddy</div>
              <div className="text-sm text-slate-500">Founder, Janma Foundation</div>
            </div>
          </div>
        </Reveal>
      </div>
    </Container>
  </section>
);

const partnerInitials = (name) =>
  name
    .split(" ")
    .filter((w) => /[A-Za-z]/.test(w))
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const PartnersStrip = () => {
  const partners = useApiData(fetchPartners, DEFAULT_PARTNERS, groupPartnersByType);
  const all = [...partners.corporate, ...partners.ngo];

  return (
    <section className="py-20 bg-ivory" data-testid="home-partners-strip">
      <Container>
        <SectionHeading
          overline="Trusted Collaborators"
          title="Powered by generous partners"
          subtitle="Corporate CSR initiatives and NGO networks that make our grassroots mission possible."
          center
        />
        <div className="flex flex-wrap justify-center items-stretch gap-4 sm:gap-5">
          {all.map((p, i) => (
            <Reveal key={p.id || p.name} delay={(i % 6) * 0.04}>
              <div className="h-24 w-40 sm:w-44 flex flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white p-3 hover:border-teal-200 hover:shadow-soft transition-all">
                {p.logo_path ? (
                  <img src={p.logo_path} alt={`${p.name} logo`} className="h-full w-full object-contain" />
                ) : (
                  <>
                    <span className="grid place-items-center h-9 w-9 shrink-0 rounded-full bg-teal-700 text-white font-display font-bold text-xs">
                      {partnerInitials(p.name)}
                    </span>
                    <span className="text-[11px] font-display font-semibold text-slate-600 text-center leading-tight line-clamp-2">
                      {p.name}
                    </span>
                  </>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/partners"
            data-testid="home-view-partners"
            className="inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-coral transition-colors"
          >
            Meet all our partners <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Container>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <Marquee />
      <ImpactStrip />
      <Manifesto />
      <FeaturedPrograms />
      <EditorialQuote />
      <PartnersStrip />
      <Testimonials />
      <DonateSection />
    </>
  );
}
